import { LoadingSpinner, type UseModalState } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useGetLastPayout } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetLastPayout";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { PAYOUT_SETTINGS_FULL_PATH } from "../../Accounts/PayoutSettings/paths";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { PageWrapper } from "../../components/PageWrapper";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { usePayoutRemainingBalance } from "../api/usePayoutRemainingBalance";
import {
  FAILED_PAYOUT_PAGE_ACTIONS,
  type FailedPayoutPageAction,
  FailedPayoutPageContent,
} from "./components/FailedPayoutPageContent";

interface FailedPayoutPageProps {
  modalState: UseModalState;
}

export function FailedPayoutPage(props: FailedPayoutPageProps) {
  const { modalState } = props;

  const history = useHistory();

  const {
    data: lastPayout,
    isLoading,
    isError,
    refetch: refetchGetLastPayout,
  } = useGetLastPayout();
  const { isLoading: isRetryPayoutLoading, mutate: retryPayout } = usePayoutRemainingBalance({
    onPayoutSuccess: async () => {
      // fetch the retried payout, it should be in pending state, which should cause FailedPayoutPage to close
      // and the FailedPayoutBannerContainer to disappear
      await refetchGetLastPayout();
    },
  });

  const isMissingPayoutData = !isLoading && !lastPayout;
  const isFailedPayout = lastPayout?.data.attributes.status === "failed";

  const shouldClose = isError || isMissingPayoutData || !isFailedPayout;

  const logEventData = {
    payoutCreatedAt: lastPayout?.data.attributes.createdAt,
    amount: lastPayout?.data.attributes.amount,
    errorCode:
      lastPayout?.data.attributes.status === "failed"
        ? lastPayout.data.attributes.errorCode
        : undefined,
    errorMessage:
      lastPayout?.data.attributes.status === "failed"
        ? lastPayout.data.attributes.errorMessage
        : undefined,
  };

  useLogEffect(APP_V2_USER_EVENTS.FAILED_PAYOUT_PAGE_OPENED, logEventData, {
    enabled: !shouldClose,
  });

  useEffect(() => {
    if (shouldClose) {
      modalState.closeModal();
    }
  }, [shouldClose, modalState]);

  if (shouldClose) {
    return null;
  }

  return (
    <PageWrapper variant="tertiary">
      <PageHeaderTopRow
        sx={(theme) => ({ ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(5) }) })}
        onClickBack={modalState.closeModal}
      />
      {isLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}

      {!isLoading && lastPayout && lastPayout.data.attributes.status === "failed" && (
        <FailedPayoutPageContent
          errorTitle={lastPayout.data.attributes.failurePageDetails.title}
          errorDescription={lastPayout.data.attributes.failurePageDetails.description}
          errorTitleShort={lastPayout.data.attributes.failurePageDetails.shortTitle}
          externalAccount={lastPayout.data.attributes.destinationDetails}
          actions={lastPayout.data.attributes.failurePageDetails.actions.filter(
            (action): action is FailedPayoutPageAction =>
              (FAILED_PAYOUT_PAGE_ACTIONS as unknown as string[]).includes(action)
          )}
          isRetryPayoutLoading={isRetryPayoutLoading}
          onRetryPayout={() => {
            logEvent(APP_V2_USER_EVENTS.FAILED_PAYOUT_ACTION_RETRY_PAYOUT, logEventData);
            retryPayout();
          }}
          onOpenPayoutSettings={() => {
            history.push(PAYOUT_SETTINGS_FULL_PATH);
            logEvent(APP_V2_USER_EVENTS.FAILED_PAYOUT_ACTION_UPDATE_PAYOUT_SETTINGS, logEventData);
          }}
          onTalkToSupport={() => {
            logEvent(APP_V2_USER_EVENTS.FAILED_PAYOUT_ACTION_TALK_TO_SUPPORT, logEventData);
            void openZendeskMessaging();
          }}
        />
      )}
    </PageWrapper>
  );
}
